import React, { useState, useEffect, useRef, useContext } from "react";
import "../../css/CRUDModal.css";
import DeleteConfirmationModal from "./DeleteConfirmationRole";
import { ReactComponent as ChevronDownIcon } from "../../assets/Icons/Chevron down.svg";
import { ReactComponent as ChevronUp } from "../../assets/Icons/ChevronUp.svg";
import CustomAlert from "../../helpers/CustomAlert";
import { toast } from "react-toastify";
import { apiList } from "../../helpers/apiList";
import { axiosPostApi } from "../../helpers/interceptorServices";

const RoleModal = ({ show, handleClose, editData, action, fetchRoleData }) => {
  const loggedInUserName = sessionStorage.getItem("username");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [smsTypeOpen, setSmsTypeOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    username: loggedInUserName,
    userRole: "",
    userProfile: "",
    roleName: "",
  });
  const [errors, setErrors] = useState({
    userRole: "",
    userProfile: "",
    roleName: "",
  });

  useEffect(() => {
    if (action === "add") {
      setFormData({
        username: loggedInUserName,
        userRole: "",
        userProfile: "",
        roleName: "",
      });
    } else if (
      action === "modify" ||
      action === "view" ||
      action === "delete"
    ) {
      setFormData({
        ...editData,
        username: loggedInUserName,
      });
    }
  }, [action, editData]);

  const smsTypeRef = useRef(null);
  const toggleSMSTypeDropdown = () => setSmsTypeOpen(!smsTypeOpen);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleClickOutside = (event) => {
    if (smsTypeRef.current && !smsTypeRef.current.contains(event.target)) {
      setSmsTypeOpen(false);
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
  };

  // Simple validation function
  const validate = () => {
    const newErrors = {};

    // Role Name validation (between 2 and 50 characters)
    formData.roleName = formData.roleName.trim();
    if (!formData.roleName) {
      newErrors.roleName = "Role Name is required";
    } else if (!/^[A-Za-z]+(?: [A-Za-z]+)*$/.test(formData.roleName)) {
      newErrors.roleName =
        "Role name must only contain alphabetic characters and spaces";
    } else if (formData.roleName.length < 2 || formData.roleName.length > 30) {
      newErrors.roleName = "Role name must be between 2 and 30 characters";
    }

    // User Role validation
    if (!formData.userRole) {
      newErrors.userRole = "Role Code is required";
    } else if (!/^[a-zA-Z0-9]+$/.test(formData.userRole)) {
      newErrors.userRole =
        "Role Code must be alphanumeric (no special characters/no empty space)";
    } else if (formData.userRole.length < 2 || formData.userRole.length > 10) {
      newErrors.userRole = "Role Code must be between 2 and 10 characters";
    } else if (Number(formData.userRole) === 0) {
      newErrors.userRole = "User Role cannot be 0";
    } else if (/^0/.test(formData.userRole)) {
      newErrors.userRole = "User Role cannot start with 0";
    }

    if (!formData.userProfile) {
      newErrors.userProfile = "Select User Profile";
    }
    return newErrors;
  };

  const handleAddRole = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      setIsSubmitted(true);
      let apiUrl = "";
      let successMessage = "";

      if (action === "add") {
        apiUrl = apiList.CREATE_ROLE_DATA; // Add
        successMessage = "User menu created successfully";
      } else if (action === "modify") {
        apiUrl = apiList.MODIFY_ROLE_DATA; // Modify
        successMessage = "User menu details modified successfully";
      }

      axiosPostApi(apiUrl, formData)
        .then((response) => {
          if (response.status === 200 && response.data.status === true) {
            toast.success(successMessage, {
              position: "top-right",
              autoClose: 2000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });
            handleClose();
            fetchRoleData(1);
          } else {
            CustomAlert({
              title:
                action === "add"
                  ? "Failed to Add Role"
                  : "Failed to Modify Role",
              text:
                response.data.message ||
                "There was an issue processing the request. Please try again.",
              icon: "error",
              confirmButtonText: "Retry",
            });
          }
        })
        .catch((error) => {
          console.error("Error processing customer request:", error);
          CustomAlert({
            title: "Error",
            text:
              action === "add"
                ? "An error occurred while adding the customer. Please try again later."
                : "An error occurred while modifying the Role. Please try again later.",
            icon: "error",
            confirmButtonText: "Retry",
          });
        });
    }
  };

  const isViewMode = action === "view";
  const isDeleteMode = action === "delete";
  const isEditMode = action === "modify";

  const handleBack = () => {
    handleClose();
    setErrors({});
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };
  return (
    <>
      <div
        className={`modal-lg modal fade ${show ? "show" : ""}`}
        tabIndex="-1"
        role="dialog"
        style={{ display: show ? "block" : "none" }}
      >
        <div className="modal-dialog modal-sm" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title main-title">
                {action.charAt(0).toUpperCase() + action.slice(1)} Role Details
              </h5>
            </div>
            <form onSubmit={(e) => handleAddRole(e)}>
              <div className="scoped-modal modal-body">
                <div className="mb-3">
                  <label htmlFor="userProfile">
                    User Profile<span style={{ color: "red" }}>*</span>
                  </label>
                  <div
                    className="form-group position-relative"
                    ref={smsTypeRef}
                  >
                    <select
                      className="form-control select-dropdown"
                      id="userProfile"
                      value={formData?.userProfile || ""}
                      disabled={isViewMode || isDeleteMode}
                      onClick={toggleSMSTypeDropdown}
                      onChange={handleInputChange}
                    >
                      <option value="" disabled>
                        Select Profile
                      </option>
                      <option value="CUSTOMER">CUSTOMER</option>
                      <option value="OPERATION">OPERATION</option>
                    </select>
                    <span className="position-absolute select-icon">
                      {smsTypeOpen ? <ChevronUp /> : <ChevronDownIcon />}
                    </span>
                  </div>
                  {!(isViewMode || isDeleteMode || isEditMode) &&
                  errors.userProfile ? (
                    <span className="error">{errors.userProfile}</span>
                  ) : null}
                </div>
                <div className="mb-3">
                  <div className="form-group">
                    <label htmlFor="userRole">
                      Role Code<span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter Role Code"
                      id="userRole"
                      value={formData?.userRole || ""}
                      disabled={isEditMode || isViewMode || isDeleteMode}
                      onChange={handleInputChange}
                    />
                    {!(isViewMode || isDeleteMode || isEditMode) &&
                    errors.userRole ? (
                      <span className="error">{errors.userRole}</span>
                    ) : null}
                  </div>
                </div>
                <div className="mb-3">
                  <div className="form-group">
                    <label htmlFor="roleName">
                      Role Name<span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="roleName"
                      value={formData?.roleName || ""}
                      disabled={isViewMode || isDeleteMode}
                      placeholder="Enter Role Name"
                      onChange={handleInputChange}
                    />
                    {!(isViewMode || isDeleteMode || isEditMode) &&
                    errors.roleName ? (
                      <span className="error">{errors.roleName}</span>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="scoped-modal modal-footer">
                <button
                  type="button"
                  className="btn btn-back"
                  onClick={handleBack}
                >
                  Back
                </button>
                {!isViewMode && !isDeleteMode && (
                  <button type="submit" className="btn btn-submit">
                    {isEditMode ? "Update" : "Submit"}
                  </button>
                )}
                {isDeleteMode && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={openModal}
                  >
                    Delete
                  </button>
                )}
                <DeleteConfirmationModal
                  isOpen={isModalOpen}
                  onClose={closeModal}
                  handleClose={handleClose}
                  editData={editData}
                  fetchRoleData={fetchRoleData}
                />
              </div>
            </form>
          </div>
        </div>
      </div>
      {show && <div className="modal-backdrop fade show"></div>}
      {isModalOpen && <div className="modal-backdrop fade show"></div>}
    </>
  );
};

export default RoleModal;
